import { Box, CircularProgress } from '@mui/material';
import React, { useState } from 'react';
import { Query } from '@features/redux/apis/utils';
import { useSystemConfigMutation, useSystemConfigQuery } from '@features/redux/apis/system';
import { ForceOffsetHeader } from './ForceOffsetHeader';
import { ForceEffects } from './ForceEffects';
import { ForceMaterials } from './ForceMaterials';
import { useAlert } from '@hooks/useAlert';
import { useSelector } from 'react-redux';
import type { RootState } from '@features/redux/store';

const DEFAULT_DRAFT = {};

const useSystemConfig = Query.withDraft(useSystemConfigQuery, DEFAULT_DRAFT);

interface ForceOffsetProps {
    stationID?: number | null;
    productId?: string | null;
    /** Kiosk-resolved MachineVariant — preferred config key (ProductID is duplicated across products) */
    machineVariant?: string | null;
    serialNumber: string | null | undefined;
    /** When true, the header is not rendered (for when InfoCard has actions prop) */
    hideHeader?: boolean;
    /** Callback to get the header actions for external placement */
    getHeaderActions?: (actions: React.ReactNode) => void;
}

export const ForceOffset = ({
    stationID,
    productId,
    machineVariant,
    serialNumber,
    hideHeader = false,
    getHeaderActions,
}: ForceOffsetProps) => {
    const { role } = useSelector(({ auth }: RootState) => auth.Roles);

    const { setAlert } = useAlert();

    const [unlocked, setUnlocked] = useState(false);

    // Call hooks before early return
    const { isLoading, draft } = useSystemConfig(
        { serialNumber: serialNumber || '' },
        { skip: !serialNumber }
    );
    const [state, dispatch] = draft;

    const [mutate, { isLoading: mutationLoading }] = useSystemConfigMutation({
        fixedCacheKey: 'ForceOffsetMutation',
    });

    const completeDisable = productId === null || productId === undefined || !role.isAdmin;

    const onSave = async () => {
        try {
            const resp: any = await mutate({
                serialNumber: serialNumber!,
                config: state!,
                stationID: stationID!,
            });

            if (resp?.error?.status) throw new Error(resp.status);
            setUnlocked(false);
        } catch (e) {
            setAlert('Failed to save configuration!', 'error');
        }
    };

    const headerActions = (
        <ForceOffsetHeader
            productId={productId!}
            disabled={completeDisable || mutationLoading}
            loading={mutationLoading}
            unlocked={unlocked}
            onSave={onSave}
            unlock={setUnlocked}
        />
    );

    // Call the callback to pass header actions to parent
    React.useEffect(() => {
        if (getHeaderActions) {
            getHeaderActions(headerActions);
        }
    }, [completeDisable, mutationLoading, unlocked, getHeaderActions, state]);

    // Early return after all hooks
    if (!serialNumber) return null;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {!hideHeader && headerActions}
            {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size='2.5rem' />
                </Box>
            ) : (
                <ForceMaterials
                    productId={productId!}
                    machineVariant={machineVariant}
                    unlocked={unlocked}
                    state={state}
                    dispatch={dispatch}
                />
            )}
            <ForceEffects />
        </Box>
    );
};
